import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Route, Trash2, Receipt, Lock, FileCheck2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { COMPANY } from '../mock';

const sections = [
  {
    icon: FileCheck2,
    title: 'Online Quotes',
    body: [
      'Prices shown by our online instant quote tool are indicative only and are based entirely on the information supplied by the customer at the time of the request (service type, property size, items, access, distance, dates and any additional details).',
      'The quoted price range is non-binding until Move Van Ltd confirms the final price in writing (by email, text, WhatsApp or another agreed method) after reviewing the booking details.',
      'The final price may vary from the online quote where the job details supplied do not accurately reflect the work required on the day, including (but not limited to) additional items, larger or heavier items than described, poor access, longer carries, parking issues, waiting time, or additional mileage.',
    ],
  },
  {
    icon: Clock,
    title: 'Booked Time & Additional Time',
    body: [
      'The price quoted is based on the amount of time booked or estimated for the job.',
      'If the job exceeds the booked time, any additional time will be charged at 1.5 times (1.5×) the applicable booked hourly rate.',
      'Additional time may include delays outside our reasonable control where our vehicle and/or team are required to remain available, including waiting for access, keys, lifts, loading or unloading.',
    ],
  },
  {
    icon: Route,
    title: 'Mileage Charge',
    body: [
      'Where mileage charges apply, mileage will be charged at £1.95 per mile.',
      'The customer will be informed where mileage is chargeable as part of their booking or quotation. Any additional mileage requested or required because of changes to the agreed collection or delivery details may result in additional charges.',
    ],
  },
  {
    icon: Trash2,
    title: 'Waste & Clearance Quotations',
    body: [
      'Prices for rubbish, waste and clearance services are based on the type, quantity, volume and description of the waste provided to us when the quotation is given.',
      'If, upon collection, the amount or type of waste is materially different from the information or photographs originally supplied, the original quotation may no longer apply. We will inform the customer of any revised charge before collecting the additional or different waste.',
      'Certain waste may be subject to additional disposal charges or may not be accepted.',
    ],
  },
  {
    icon: Receipt,
    title: 'Payment & Invoices',
    body: [
      'By agreeing to a booking, the customer agrees to pay all charges properly due for the services provided in accordance with these terms.',
      'Invoices must be paid by the payment deadline stated on the invoice or otherwise agreed with Move Van Ltd.',
      'Additional charges incurred during the job, including additional time, mileage, waiting time, additional items or additional waste, may be added to the final invoice where applicable.',
    ],
  },
  {
    icon: Lock,
    title: 'Unpaid Charges & Storage',
    body: [
      'Where goods cannot reasonably be delivered or released because payment properly due under the agreed booking remains outstanding, and where permitted by law, Move Van Ltd may arrange for the goods to be placed into secure storage.',
      'Storage will be charged at £25 per day, in addition to any outstanding invoice and any reasonable transport, handling or storage-related costs incurred.',
      "Any storage or retention of a customer's goods will be handled in accordance with applicable law. Move Van Ltd will not dispose of or sell a customer's goods except where legally entitled to do so and after any notices required by law have been given.",
    ],
  },
  {
    icon: FileCheck2,
    title: 'Acceptance of Terms',
    body: [
      'Confirmation of a booking by telephone, text message, WhatsApp, email, website or another agreed method constitutes acceptance of these Terms & Conditions.',
      'Customers should review the quotation, booking details and these Terms & Conditions before confirming their booking.',
    ],
  },
];

const Terms = () => {
  return (
    <>
      <PageHeader
        crumb="Terms & Conditions"
        title="Terms & Conditions"
        subtitle="Booking, charges & payment terms for all services provided by Move Van Ltd."
      />

      <section className="section">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky side nav */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <div className="rounded-xl border border-slate-200 bg-[color:var(--mv-sky)] p-6">
              <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">On this page</div>
              <ol className="space-y-2 text-sm">
                {sections.map((s, i) => (
                  <li key={s.title}>
                    <a href={`#section-${i + 1}`} className="flex items-start gap-2 text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)] font-semibold transition-colors">
                      <span className="w-6 h-6 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-6 pt-5 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
                By confirming or agreeing to a booking with Move Van Ltd, the customer agrees to the following terms and accepts responsibility for all charges properly due under the booking.
              </div>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-8 space-y-10">
            <div className="prose max-w-none">
              <p className="text-slate-700 text-lg leading-relaxed">
                These Terms &amp; Conditions govern bookings made with <span className="font-semibold text-[color:var(--mv-navy)]">Move Van Ltd</span> for removals, man &amp; van, delivery and clearance services. Please read them carefully before confirming your booking.
              </p>
            </div>

            {sections.map((s, i) => {
              const Icon = s.icon;
              return (
                <article id={`section-${i + 1}`} key={s.title} className="rounded-xl border border-slate-200 bg-white p-7 md:p-8 shadow-[0_20px_40px_-30px_rgba(11,37,69,0.25)] scroll-mt-28">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-md bg-[color:var(--mv-navy)] text-white flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[color:var(--mv-orange)]">Section {String(i + 1).padStart(2, '0')}</div>
                      <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[color:var(--mv-navy)] leading-tight">{s.title}</h2>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4 text-slate-700 leading-relaxed">
                    {s.body.map((p, idx) => (
                      <p key={`${s.title}-${idx}`}>{p}</p>
                    ))}
                  </div>
                </article>
              );
            })}

            <div className="rounded-xl bg-[color:var(--mv-navy)] text-white p-8 md:p-10">
              <div className="font-display text-2xl md:text-3xl font-extrabold">Move Van Ltd</div>
              <div className="text-white/70 mt-1">Moving Made Simple.</div>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/85">
                <div>
                  <div className="text-white/50 uppercase tracking-wider text-xs font-bold">Address</div>
                  <div className="mt-1">{COMPANY.address}</div>
                </div>
                <div>
                  <div className="text-white/50 uppercase tracking-wider text-xs font-bold">Phone</div>
                  <div className="mt-1">{COMPANY.phone}</div>
                </div>
                <div>
                  <div className="text-white/50 uppercase tracking-wider text-xs font-bold">Email</div>
                  <div className="mt-1 break-all">{COMPANY.email}</div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/#quote" className="btn-primary">Request a quote <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/prices" className="btn-outline">View our prices</Link>
              </div>
              <div className="mt-6 text-xs text-white/50">
                Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Terms;
